import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [data, setData] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt > 8) { setState("timeout"); return; }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      setData(res.data);
      if (res.data.payment_status === "paid") { setState("paid"); return; }
      if (["expired", "failed"].includes(res.data.payment_status)) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-cream pt-24 px-6">
      <SEO title="Payment Confirmation | Beyond Blessed" description="Payment confirmation for Beyond Blessed Consulting & Counseling." path="/payment/success" noindex />
      <div className="max-w-lg text-center">
        {state === "checking" && (
          <><Loader2 className="animate-spin text-teal mx-auto mb-6" size={48} />
          <h1 className="font-serif text-3xl text-navy">Confirming your payment…</h1>
          <p className="text-charcoal/60 mt-3">Please wait a moment.</p></>
        )}
        {state === "paid" && (
          <><CheckCircle2 className="text-teal mx-auto mb-6" size={64} />
          <p className="font-script text-4xl text-gold mb-2">You're registered!</p>
          <h1 className="font-serif text-4xl text-navy mb-4">Welcome to Renewed &amp; Restored</h1>
          <p className="text-charcoal/70 mb-2">{data?.plan_label} — ${data?.amount?.toFixed?.(2)}</p>
          <p className="text-charcoal/70 mb-8">A confirmation has been sent. We can't wait to see you in Clearwater.</p>
          <Link to="/retreats" data-testid="success-back" className="bg-navy text-cream px-8 py-4 rounded-full inline-block hover:bg-teal transition-colors">Back to Retreat</Link></>
        )}
        {(state === "error" || state === "timeout") && (
          <><XCircle className="text-gold mx-auto mb-6" size={56} />
          <h1 className="font-serif text-3xl text-navy mb-3">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-charcoal/70 mb-8">{state === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation or contact us." : "We couldn't confirm your payment. Please try again."}</p>
          <Link to="/retreats" data-testid="success-retry" className="bg-navy text-cream px-8 py-4 rounded-full inline-block hover:bg-teal transition-colors">Return to Retreat</Link></>
        )}
      </div>
    </div>
  );
}
